import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../../../../../auth/AuthContext";
import { getFundDbName } from "../../../../../utils/funds";
import { baixaService } from "../services/baixa-service";
import { EntregoOpsItem, EntregoBaixaItem } from "../types";

export const entregoBaixaKeys = {
  all: ["entrego-baixa"] as const,
  ops: (dbName: string) => [...entregoBaixaKeys.all, "ops", dbName] as const,
  list: (dbName: string, selectedOps: string[]) =>
    [...entregoBaixaKeys.all, "list", dbName, selectedOps.join(",")] as const,
};

export function useEntregoBaixa() {
  const { user } = useAuth();
  const [selectedOps, setSelectedOps] = useState<string[]>([]);

  const xamaDbName = getFundDbName("entrego");

  const {
    data: opsList = [],
    isPending: isPendingOps,
    refetch: refetchOps,
  } = useQuery({
    queryKey: entregoBaixaKeys.ops(xamaDbName),
    queryFn: async () => {
      const response = await baixaService.getEntregoBaixaList({
        dbName: xamaDbName,
        opsMae: "entrego",
        type: "ops",
      });
      return (response.data || []) as EntregoOpsItem[];
    },
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 120,
  });

  const {
    data: baixaList = [],
    isPending: isPendingBaixas,
    refetch: refetchBaixas,
  } = useQuery({
    queryKey: entregoBaixaKeys.list(xamaDbName, selectedOps),
    queryFn: async () => {
      const response = await baixaService.getEntregoBaixaList({
        dbName: xamaDbName,
        opsMae: "entrego",
        type: "baixa",
        listOps: selectedOps,
      });
      return (response.data || []) as EntregoBaixaItem[];
    },
    enabled: selectedOps.length > 0,
    staleTime: 1000 * 60 * 2,
  });

  const startDocMutation = useMutation({
    mutationFn: async ({
      filterDate,
      listOps,
    }: {
      filterDate: string;
      listOps: string[];
    }) => {
      if (!xamaDbName || !user?.email) throw new Error("Missing required data");
      return baixaService.startBaixaDoc({
        dbName: xamaDbName,
        filterDate,
        ops: "entrego",
        listOps,
        userInfo: { email: user.email },
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetchBaixas();
      }, 3000);
    },
  });

  const submitDbMutation = useMutation({
    mutationFn: async ({
      filterDate,
      listOps,
    }: {
      filterDate: string;
      listOps: string[];
    }) => {
      if (!xamaDbName || !user?.email) throw new Error("Missing required data");
      return baixaService.submitBaixaDb({
        dbName: xamaDbName,
        filterDate,
        ops: "entrego",
        listOps,
        userInfo: { email: user.email },
      });
    },
    onSuccess: () => {
      setTimeout(() => {
        refetchBaixas();
      }, 3000);
    },
  });

  const startDoc = (filterDate: string, listOps: string[]) => {
    startDocMutation.mutate({ filterDate, listOps });
  };

  const submitDb = (filterDate: string, listOps: string[]) => {
    submitDbMutation.mutate({ filterDate, listOps });
  };

  return {
    xamaDbName,
    opsList,
    baixaList,
    selectedOps,
    setSelectedOps,
    isLoadingOps: isPendingOps,
    isLoadingBaixas: isPendingBaixas,
    startDoc,
    submitDb,
    isStartingDoc: startDocMutation.isPending,
    isSubmittingDb: submitDbMutation.isPending,
    refetchOps,
    refetchBaixas,
  };
}
